import { format, parse } from 'date-fns';

const formatAsDate = (dateString?: Date | string): string => {
  if (!dateString) {
    return '';
  }

  return format(dateString, 'DD/MM/YYYY');
};

const formatAsDayDate = (dateString?: Date | null | string): string => {
  if (!dateString) {
    return '';
  }

  return format(dateString, 'ddd - DD/MM/YYYY');
};

const formatAsDayDateTime = (dateString?: Date): string => {
  if (!dateString) {
    return '';
  }

  return format(dateString, 'ddd - DD/MM/YYYY - HH:mm');
};

const formatAsDateTime = (dateString?: Date | string): string => {
  if (!dateString) {
    return '';
  }

  return format(dateString, 'DD/MM/YYYY - HH:mm:ss');
};

const formatAsFullDateTime = (dateString?: Date): string => {
  if (!dateString) {
    return '';
  }

  return format(dateString, 'ddd - DD/MM/YYYY - HH:mm:ss');
};

const getTime = (dateString?: Date | string): string => {
  if (!dateString) {
    return '';
  }

  return format(dateString, 'HH:mm:ss');
};

const getTimeNoSec = (dateString?: Date | string): string => {
  if (!dateString) {
    return '';
  }

  return format(dateString, 'HH:mm');
};

const getMonthAndDay = (dateString: Date | string): string => {
  if (!dateString) {
    return '';
  }

  return format(dateString, 'MMM D');
};

const getDay = (dateString: Date): string => {
  if (!dateString) {
    return '';
  }

  return format(dateString, 'ddd');
};

const formatToSafeISODate = (dateString?: Date): string => {
  if (!dateString) {
    return '';
  }

  return format(dateString, 'YYYY-MM-DDT00:00:00Z');
};

const formatToWithoutTime = (dateString?: Date): string => {
  if (!dateString) {
    return '';
  }

  return format(dateString, 'YYYY-MM-DD', { locale: '' });
};

// Checks if date is in DD/MM/YYYY or DD-MM-YYYY format, does not check for leap years
const BASIC_DATE_REGEX = new RegExp(/^(0[1-9]|[12][0-9]|3[01])[/-](0[1-9]|1[012])[/-](\d{4})$/);

// SQL Server CONVERT(varchar, datetime, 100) output, e.g. "May  7 2025  6:17PM" — no space before
// AM/PM makes it unparseable by `new Date`. Some backend jobs store metadata dates in this format.
const SQL_SERVER_DATE_REGEX = new RegExp(/^([A-Z][a-z]{2})\s+(\d{1,2})\s+(\d{4})\s+(\d{1,2}):(\d{2})(AM|PM)$/);
const SQL_SERVER_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const legacySqlServerDateParser = (dateString: string): Date | undefined => {
  const match = dateString.match(SQL_SERVER_DATE_REGEX);
  if (!match) {
    return undefined;
  }
  const [, monthName, day, year, hour, minute, meridiem] = match;
  const monthIndex = SQL_SERVER_MONTHS.indexOf(monthName);
  if (monthIndex === -1) {
    return undefined;
  }
  const hours = (Number(hour) % 12) + (meridiem === 'PM' ? 12 : 0);

  return new Date(Number(year), monthIndex, Number(day), hours, Number(minute));
};

const dateParser = (dateString: string): Date | undefined => {
  // react-day-picker calls parseDate with its raw `value` prop (e.g. an Invalid Date object)
  // when that value isn't a valid Date, so this can receive non-string input at runtime
  if (typeof dateString !== 'string') {
    return undefined;
  }

  const match = dateString.match(BASIC_DATE_REGEX);
  if (match) {
    const [day, month, year] = match.slice(1);

    return parse(`${year}-${month}-${day}T00:00:00Z`);
  }

  return undefined;
};

export {
  dateParser,
  legacySqlServerDateParser,
  formatAsDate,
  formatAsDateTime,
  formatAsDayDate,
  formatAsDayDateTime,
  formatToWithoutTime,
  formatAsFullDateTime,
  formatToSafeISODate,
  getDay,
  getMonthAndDay,
  getTime,
  getTimeNoSec,
};
